import { useFormContext } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { useDocumentUpload } from "./document-upload/useDocumentUpload";
import { useDocumentList } from "./document-upload/useDocumentList";
import { IncomeTypeSelection } from "./document-upload/IncomeTypeSelection";
import { FileUploadZone } from "./document-upload/FileUploadZone";
import { FileList } from "./document-upload/FileList";
import { DocumentRequirements } from "./document-upload/DocumentRequirements";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";

type IncomeType = 'employment' | 'investment' | 'rental' | 'other';

export function DocumentUploadSection() {
  const [selectedIncomeTypes, setSelectedIncomeTypes] = useState<IncomeType[]>([]);
  const [otherIncome, setOtherIncome] = useState("");
  const form = useFormContext();
  const { toast } = useToast();
  
  const taxReturnId = form.getValues('id');
  const financialYear = form.getValues('financial_year');
  const { isUploading, uploadProgress, uploadDocument } = useDocumentUpload(taxReturnId);
  const { files, removeDocument, refreshFiles } = useDocumentList(taxReturnId);

  // Ensure we have a tax return ID
  useEffect(() => {
    if (!taxReturnId) {
      toast({
        title: "Error",
        description: "Please complete the previous sections first",
        variant: "destructive",
      });
    }
  }, [taxReturnId, toast]);

  const handleIncomeTypeChange = (type: IncomeType) => {
    setSelectedIncomeTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      if (!selectedIncomeTypes.length) {
        throw new Error('Please select at least one income type');
      }

      if (!taxReturnId) {
        throw new Error('No tax return request found. Please try again.');
      }

      const file = files[0];
      const result = await uploadDocument(
        file,
        selectedIncomeTypes.join(','),
        user.id,
        financialYear
      );

      if (result) {
        await refreshFiles();
      }
    } catch (error: any) {
      console.error('Error handling file upload:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload document",
        variant: "destructive",
      });
    }
    
    // Reset the input
    event.target.value = '';
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <Card>
        <CardContent className="pt-6">
          <IncomeTypeSelection
            selectedTypes={selectedIncomeTypes}
            otherIncome={otherIncome}
            onTypeChange={handleIncomeTypeChange}
            onOtherIncomeChange={setOtherIncome}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <FileUploadZone
              onFileSelect={handleFileUpload}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              disabled={selectedIncomeTypes.length === 0 || !taxReturnId}
            />

            <FileList
              files={files}
              onRemove={removeDocument}
              isUploading={isUploading}
            />

            <DocumentRequirements />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}